import React from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useIntl } from "react-intl";
import { useLocation } from 'react-router';
import { JSEncrypt } from 'jsencrypt';
import { Checkbox, FormControlLabel, Typography, withStyles, Container, Button, Box } from '@material-ui/core';
import { debounce } from 'lodash';

import { FormTextField, FormDialog, Loading, AlertMessages, AlertDialog } from '../../components';
import Branding from '../../components/Header/Branding';

import { getAlternateLogo } from "../../utils";
import { getI18n } from "../../utils/intl";
import { ALERT_ERROR } from "../../constants";
import { ROUTES } from "../../constants/routes";
import { getThemes, getCmsSettings, getUserContexts } from '../../reducers/portalConfig';
import {
  getIsLoading,
  getPublicKey,
  getPublicKeyError,
  getRegexPolicy,
  getRegexError,
} from '../../reducers/userProfile';
import { processAccountSetup, getAccountSetupData } from "../../actions/accountSetup";
import { fetchPasswordPolicy, fetchPublicKey } from "../../actions/userProfile";

import { accountSetupReducer, initializeAccountSetupState, ACTIONS, validateFormInputs } from './reducer';
import styles from './styles';
import PasswordPolicy from "../../pageComponents/PasswordPolicy";
import { terminateAllSessions } from '../../actions/newPassword';

const labels = {
  header: "label.custom.pages.select.account.setup",
  firstName: "label.user.profile.firstname",
  lastName: "label.user.profile.lastname",
  userName: "label.user.profile.username",
  email: "label.user.profile.email",
  password: "label.user.account.setup.password.label",
  confirmPassword: "label.user.account.setup.password.confirm.label",
  termsOfUsePre: "label.user.account.setup.termsOfUse.helptext.pre",
  termsOfUse: "label.settings.registrationsettings.registration.termsOfUse",
  submitButton: "label.user.account.setup.ok.button",
  cancelButton: "label.cancel.button",
  okButton: "label.ok.button",
  termsOfUseTitle: "label.settings.registrationsettings.registration.termsOfUse",
  loadingError: "label.user.account.setup.error.load",
  unsavedChangesTitle: "label.dialog.unsaved.changes.title",
  unsavedChangesDescription: "label.dialog.unsaved.changes.description",
  unsavedChangesCancelText: "label.dialog.unsaved.changes.cancel",
};

const helpTexts = {
  firstName: "label.user.details.textfields.helptext",
  lastName: "label.user.details.textfields.helptext",
  userName: "label.user.account.setup.username.helptext",
  email: "label.user.account.setup.email.helptext",
};

const validateFieldValue = debounce(({ field, dispatch, intl, regexPolicy }) => {
  dispatch({ type: ACTIONS.UPDATE_FIELD_ERRORS, payload: { field, intl, regexPolicy } })
}, 300);

const AccountSetup = ({
  classes, logo, accountSetup, regexPolicy, publicKey, regexError, alternateLogo,
  termsOfUseContent, publicKeyError, isLoadingPublicKeyAndPasswordPolicy,
  fetchPublicKey, getAccountSetupData, processAccountSetup, fetchPasswordPolicy, userContext,
}) => {
  const intl = getI18n(useIntl());
  const { hash } = useLocation();

  const [state, dispatch] = React.useReducer(
    accountSetupReducer, null, initializeAccountSetupState,
  );
  const [notification, setNotification] = React.useState({ status: '', message: '' });
  const [isTermsOfUseModalOpen, setIsTermsOfUseModalOpen] = React.useState(false);
  const [isFormDirty, setIsFormDirty] = React.useState(false);
  const [isUnsavedAlertDialogOpen, setIsUnsavedAlertDialogOpen] = React.useState(false);
  const [isUserContextExists, setIsUserContextExists] = React.useState(false);


  React.useEffect(() => {
    fetchPublicKey();
  }, []);

  React.useEffect(() => {
    const hashArray = hash.split("/");
    if (hashArray.length >= 2 && hashArray[0].includes("token")) {
      getAccountSetupData(hashArray[1]);
    }
  }, [hash]);

  React.useEffect(() => {
    if (userContext) {
      setIsUserContextExists(true);
    } else {
      setIsUserContextExists(false);
    }
  }, [userContext])

  React.useEffect(() => {
    if (publicKeyError || regexError) {
      setNotification({ status: ALERT_ERROR, message: intl.getI18nMessage(labels.loadingError) })
    }
  }, [publicKeyError, regexError]);

  React.useEffect(() => {
    if (accountSetup.initialData.error) {
      window.location.href = ROUTES.invalidToken.path;
    }
    if (accountSetup.initialData.data) {
      dispatch({
        type: ACTIONS.UPDATE_FORM_VALUES,
        payload: { intl, regexPolicy, data: accountSetup.initialData.data },
      });
    }
  }, [accountSetup.initialData]);

  React.useEffect(() => {
    if (accountSetup.postSetupData.data) {
      window.location.href = ROUTES.dashboardRedirect.path;
      return;
    }
    if (accountSetup.postSetupData.error !== null) {
      if (accountSetup.postSetupData.error === '') {
        setNotification({ status: ALERT_ERROR, message: intl.getI18nMessage("label.unknown.error") });
        return;
      }

      if (accountSetup.postSetupData.error?.errorCode) {
        setNotification({
          status: ALERT_ERROR,
          message: intl.getI18nMessage("label.error.code",
            { code: accountSetup.postSetupData.error.errorCode }),
        });
        return;
      }

      if (!userContext && accountSetup.postSetupData.error?.invalidToken) {
        window.location.href = ROUTES.invalidToken.path;
        return;
      }

      setNotification({ status: ALERT_ERROR, message: accountSetup.postSetupData.error });
    }
  }, [accountSetup.postSetupData]);

  const handleChange = (field, value) => {
    dispatch({ type: ACTIONS.UPDATE_FIELD_VALUE, payload: { field, value } });
    validateFieldValue({ field, dispatch, intl, regexPolicy });
    setIsFormDirty(true);
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    const { validatedState, isFormValid } = validateFormInputs({ state, intl, regexPolicy });
    if (isFormValid) {
      const allFields = ['firstName', 'lastName', 'userName', 'email', 'password'];
      const payload = {
        token: accountSetup.initialData.data.token,
        uuid: accountSetup.initialData.data.uuid,
      };
      allFields.forEach(field => {
        payload[field] = state[field].value
      })
      if (publicKey) {
        const encrypt = new JSEncrypt();
        encrypt.setPublicKey(publicKey);
        payload.password = encrypt.encrypt(payload.password);
      }
      processAccountSetup(payload, publicKey);
    } else {
      dispatch({ type: ACTIONS.UPDATE_FORM_ERRORS, payload: validatedState });
    }
  }

  const onCancel = () => {
    if (isFormDirty) {
      setIsUnsavedAlertDialogOpen(true);
    } else {
      window.location.href = ROUTES.homeRedirect.path;
    }
  };

  const onUnsavedAlertDialogClose = () => {
    setIsUnsavedAlertDialogOpen(false);
  };

  const onUnsavedAlertDialogSubmit = () => {
    window.location.href = ROUTES.homeRedirect.path;
  };

  const showLoadingOverlay =
    accountSetup.initialData.isLoading
    || accountSetup.postSetupData.isLoading
    || isLoadingPublicKeyAndPasswordPolicy
    || !regexPolicy;

  return <Container className={classes.outerContainer}>
    {showLoadingOverlay && <Loading pageLoader fullPage />}
    {
      <FormDialog
        isOpen={isUserContextExists}
        hideCancelButton
        title={intl.getI18nMessage("label.new.password.session.detect.message")}
        submitText={intl.getI18nMessage("label.new.password.session.detect.terminate.button.text")}
        paperClass={classes.formDialog}
        onSubmit={() => terminateAllSessions()}
      >
        <Typography>{intl.getI18nMessage("label.new.password.session.detect.description.message")}</Typography>
        <Typography className={classes.formDialogDescription}>{intl.getI18nMessage("label.new.password.session.detect.description.help.text")}</Typography>
      </FormDialog>
    }
    <Box className={classes.contentContainer}>
      <Box className={classes.header}>
        {logo ? <Branding logo={logo} /> : <Typography variant='h1' className={classes.alternateLogo}>
          {alternateLogo}
        </Typography>}
        <Typography
          data-apim-test={'accountSetup-header'}
          variant='h1' className={classes.headerText}
        >
          {intl.getI18nMessage(labels.header)}
        </Typography>
      </Box>
      <form onSubmit={(e) => handleSubmit(e)}>
        <FormTextField
          id='accountSetup-firstName'
          name={intl.getI18nMessage(labels.firstName)}
          textFieldClass={classes.textField}
          fieldContainerClass={classes.fieldContainer}
          helperText={
            state.firstName.errors.length > 0
              ? state.firstName.errors[0]
              : intl.getI18nMessage(helpTexts.firstName)
          }
          errorHelperText={state.firstName.errors.length > 0}
          error={state.firstName.errors.length > 0}
          value={state.firstName.value}
          handleChange={value => handleChange('firstName', value)}
          maxLength={60}
        />
        <FormTextField
          id='accountSetup-lastName'
          name={intl.getI18nMessage(labels.lastName)}
          textFieldClass={classes.textField}
          fieldContainerClass={classes.fieldContainer}
          helperText={
            state.lastName.errors.length > 0
              ? state.lastName.errors[0]
              : intl.getI18nMessage(helpTexts.lastName)
          }
          errorHelperText={state.lastName.errors.length > 0}
          error={state.lastName.errors.length > 0}
          value={state.lastName.value}
          handleChange={value => handleChange('lastName', value)}
          maxLength={60}
        />
        <FormTextField
          id='accountSetup-userName'
          name={intl.getI18nMessage(labels.userName)}
          textFieldClass={classes.textField}
          fieldContainerClass={classes.fieldContainer}
          helperText={
            state.userName.errors.length > 0
              ? state.userName.errors[0]
              : intl.getI18nMessage(helpTexts.userName)
          }
          errorHelperText={state.userName.errors.length > 0}
          error={state.userName.errors.length > 0}
          value={state.userName.value}
          handleChange={value => handleChange('userName', value)}
          maxLength={60}
        />
        <FormTextField
          id='accountSetup-email'
          name={intl.getI18nMessage(labels.email)}
          textFieldClass={classes.textField}
          fieldContainerClass={classes.fieldContainer}
          helperText={
            state.email.errors.length > 0
              ? state.email.errors[0]
              : intl.getI18nMessage(helpTexts.email)
          }
          errorHelperText={state.email.errors.length > 0}
          error={state.email.errors.length > 0}
          value={state.email.value}
          disabled={true}
        />
        <FormTextField
          id='accountSetup-password'
          type='password'
          name={intl.getI18nMessage(labels.password)}
          textFieldClass={classes.textField}
          fieldContainerClass={classes.fieldContainer}
          helperText={state.password.errors.length > 0 ? state.password.errors[0] : null}
          errorHelperText={state.password.errors.length > 0}
          error={state.password.errors.length > 0}
          value={state.password.value}
          handleChange={value => handleChange('password', value)}
        />

        <PasswordPolicy
          regexPolicy={regexPolicy}
          fetchPasswordPolicy={fetchPasswordPolicy}
        />

        <FormTextField
          id='accountSetup-confirmPassword'
          type='password'
          name={intl.getI18nMessage(labels.confirmPassword)}
          textFieldClass={classes.textField}
          fieldContainerClass={classes.fieldContainer}
          helperText={
            state.confirmPassword.errors.length > 0
              ? state.confirmPassword.errors[0]
              : null
          }
          errorHelperText={state.confirmPassword.errors.length > 0}
          error={state.confirmPassword.errors.length > 0}
          value={state.confirmPassword.value}
          handleChange={value => handleChange('confirmPassword', value)}
        />

        <FormControlLabel
          control={
            <Checkbox
              id="accountSetup-termsOfUse"
              data-apim-test="accountSetup-termsOfUse"
              onChange={(event) => handleChange('termsOfUse', event.target.checked)}
              checked={state.termsOfUse.value}
            />
          }
          label={
            <>
              {intl.getI18nMessage(labels.termsOfUsePre)}
              <Button variant='text' color='secondary' className={classes.termsOfUseButton} onClick={() => setIsTermsOfUseModalOpen(true)}>
                {intl.getI18nMessage(labels.termsOfUse)}
              </Button>
            </>
          }
          classes={{
            root: classes.termsOfUse,
            label: classes.termsOfUseLabel,
          }}
        />
        <div
          className={
            `${classes.termsofUseError} ${state.termsOfUse.errors.length > 0
              ? classes.showTermsofUseError : ''}`
          }
        >
          {state.termsOfUse.errors[0]}
        </div>

        <Box className={classes.actionButtons}>
          <Button
            data-apim-test="accountSetup-submit" variant="contained"
            color="secondary" type='submit'
            disabled={Boolean(regexError) || accountSetup.postSetupData.isLoading}
            className={classes.submitButton}
          >
            {intl.getI18nMessage(labels.submitButton)}
          </Button>
          <Button
            data-apim-test="accountSetup-cancel"
            variant="outlined" className={classes.cancelButton}
            color='secondary' onClick={onCancel}
          >
            {intl.getI18nMessage(labels.cancelButton)}
          </Button>
        </Box>
      </form>
    </Box>

    <FormDialog
      dialogId="accountSetup-termsOfUse-dialog"
      title={intl.getI18nMessage(labels.termsOfUseTitle)}
      submitText={intl.getI18nMessage(labels.okButton)}
      hideCancelButton
      isOpen={isTermsOfUseModalOpen}
      onSubmit={() => setIsTermsOfUseModalOpen(false)}
    >
      {termsOfUseContent.split('&#13;').map(text => (<>{text}<br /></>))}
    </FormDialog>

    {notification.message && (
      <AlertMessages
        className={classes.notification}
        id="notificationId"
        data-apim-test="notificationId"
        variant={notification.status}
        message={notification.message}
        onClose={() => setNotification({ status: "", message: "" })}
      />
    )}

    <AlertDialog
      isOpen={isUnsavedAlertDialogOpen}
      title={intl.getI18nMessage(labels.unsavedChangesTitle)}
      description={intl.getI18nMessage(labels.unsavedChangesDescription)}
      cancelText={intl.getI18nMessage(labels.unsavedChangesCancelText)}
      onClose={onUnsavedAlertDialogClose}
      onSubmit={onUnsavedAlertDialogSubmit}
      onCancel={onUnsavedAlertDialogClose}
      dialogId="accountsetup-unsaved-dialog"
      submitText={intl.getI18nMessage('label.ok.button')}
    />
  </Container>
}

const mapStateToProps = (state) => {
  const { logo, siteTitle } = getThemes(state);
  const { REGISTRATION_TERMS_OF_USE = '' } = getCmsSettings(state) || {};

  return {
    accountSetup: state.accountSetup,
    logo,
    alternateLogo: getAlternateLogo(siteTitle),
    isLoadingPublicKeyAndPasswordPolicy: getIsLoading(state),
    regexPolicy: getRegexPolicy(state),
    regexError: getRegexError(state),
    publicKey: getPublicKey(state),
    publicKeyError: getPublicKeyError(state),
    termsOfUseContent: REGISTRATION_TERMS_OF_USE,
    userContext: getUserContexts(state),
  };
};

const mapDispatchToProps = {
  processAccountSetup,
  getAccountSetupData,
  fetchPublicKey,
  fetchPasswordPolicy,
};

AccountSetup.propTypes = {
  classes: PropTypes.object,
  logo: PropTypes.string,
  alternateLogo: PropTypes.string,
  accountSetup: PropTypes.object,
  isLoadingPublicKeyAndPasswordPolicy: PropTypes.bool,
  termsOfUseContent: PropTypes.string,
  publicKey: PropTypes.string,
  publicKeyError: PropTypes.object,
  regexPolicy: PropTypes.object,
  regexError: PropTypes.object,
  processAccountSetup: PropTypes.func,
  getAccountSetupData: PropTypes.func,
  fetchPublicKey: PropTypes.func,
  fetchPasswordPolicy: PropTypes.func,
  userContext: PropTypes.object,
};

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(AccountSetup);
